import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FavoriteIcon from '@mui/icons-material/Favorite';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { format, parseISO, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useHistory } from 'react-router-dom';

import { useFollowingViewContext } from '../../Wallet/FollowingView/FollowingViewContext';
import { useUnfollowFic } from '../../hooks/useUnfollowFic';

function formatCompetencyDate(competencyDate?: string) {
  if (!competencyDate) return '—';

  try {
    return format(startOfMonth(parseISO(competencyDate)), 'MMM/yyyy', {
      locale: ptBR,
    });
  } catch {
    return '—';
  }
}

export function FicFidcCockpitHeader() {
  const history = useHistory();
  const {
    operationId,
    operationName,
    indicatorsCompetencyDate,
    handleIndicatorsCompetencyDateChange,
    onCompleteView,
  } = useFollowingViewContext();
  const { mutate: unfollow, isLoading: isUnfollowing } = useUnfollowFic({
    onSuccess: () => history.replace('/monitor?tab=following'),
  });
  const formattedCompetencyDate = formatCompetencyDate(
    indicatorsCompetencyDate,
  );

  return (
    <Box
      id="fic-fidc-cockpit-header"
      component="header"
      aria-label="Cabeçalho do cockpit do FIC seguido"
      sx={{
        height: { xs: 'auto', md: 80 },
        flexShrink: 0,
        mb: { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', md: 'flex-start' },
        gap: 2,
        overflow: 'hidden',
      }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h2" variant="h5" noWrap>
          {operationName
            ? operationName.toUpperCase()
            : 'Cockpit do FIC seguido'}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: { xs: '100%', md: 640 } }}>
          Um resumo para acompanhar rapidamente PL, concentração e composição
          antes de aprofundar na visão completa.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'flex-start', md: 'flex-end' },
          flexShrink: 0,
          gap: 0.5,
        }}>
        <Box
          data-testid="fic-fidc-cockpit-actions"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Parar de seguir este FIC" arrow>
            <span>
              <IconButton
                aria-label={`Parar de seguir ${operationName || 'este FIC'}`}
                disabled={isUnfollowing}
                onClick={() => unfollow(operationId)}
                size="small"
                sx={{
                  color: '#E66A00',
                  '&:hover': {
                    bgcolor: '#FFF4EA',
                    color: '#E66A00',
                  },
                  '&.Mui-focusVisible': {
                    outline: '2px solid #E66A00',
                    outlineOffset: 2,
                  },
                  '&.Mui-disabled': {
                    color: 'action.disabled',
                  },
                }}>
                {isUnfollowing ? (
                  <CircularProgress size={18} aria-label="Parando de seguir" />
                ) : (
                  <FavoriteIcon fontSize="small" aria-hidden="true" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
            onClick={onCompleteView}>
            Ver completo
          </Button>
        </Box>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            aria-label="Competência anterior"
            onClick={(e) =>
              handleIndicatorsCompetencyDateChange(e, 'previous')
            }>
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" noWrap>
              Competência
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {formattedCompetencyDate}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Próxima competência"
            onClick={(e) => handleIndicatorsCompetencyDateChange(e, 'next')}>
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
